import { useState } from "react";
import {
  flagSvgUrl,
  isMultiGeo,
  normalizeFlagCode,
  primaryFlagCode,
} from "../lib/flags";
import { IconGlobeSoft } from "./Icons";

type Shape = "rounded" | "rect" | "circle";

type FlagProps = {
  code?: string | null;
  /** 宽（4×3 比例时高自动算）；circle 时为正方形边长 */
  size?: number;
  height?: number;
  shape?: Shape;
  className?: string;
  alt?: string;
};

/**
 * 国家 SVG 国旗 — 对齐 Flutter SafeCountryFlag + Web Flag
 * 本地 /flags → CDN → 字母 fallback；无 emoji
 */
export function Flag({
  code,
  size = 44,
  height,
  shape = "rounded",
  className = "",
  alt,
}: FlagProps) {
  const [attempt, setAttempt] = useState<0 | 1 | 2 | 3>(0);
  const iso = normalizeFlagCode(code);
  const w = size;
  const h = height ?? (shape === "circle" ? size : Math.round(size * (32 / 44)));
  const src = attempt < 3 && iso ? flagSvgUrl(iso, attempt === 0 ? 0 : attempt === 1 ? 1 : 2) : null;

  const radius =
    shape === "circle" ? "50%" : shape === "rounded" ? Math.max(6, Math.min(w, h) * 0.22) : 2;

  if (!src || !iso) {
    return (
      <span
        className={`flag-chrome flag-fallback ${className}`}
        style={{ width: w, height: h, borderRadius: radius, fontSize: Math.max(9, h * 0.32) }}
        aria-hidden
      >
        {(code || "·").toString().slice(0, 2).toUpperCase()}
      </span>
    );
  }

  return (
    <span
      className={`flag-chrome ${className}`}
      style={{ width: w, height: h, borderRadius: radius }}
    >
      <img
        className="flag-img"
        src={src}
        alt={alt || `${iso.toUpperCase()} flag`}
        width={w}
        height={h}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setAttempt((a) => (a >= 2 ? 3 : ((a + 1) as 0 | 1 | 2 | 3)))}
        style={{ borderRadius: radius }}
      />
    </span>
  );
}

/** 套餐/商品卡左侧：单国旗 / 多国地球 / 缺省 SIM 感地球 */
export function ProductFlag({
  geoCodes,
  geoType,
  countryHint,
  size = 44,
  className = "",
}: {
  geoCodes?: string[] | null;
  geoType?: string | null;
  countryHint?: string | null;
  size?: number;
  className?: string;
}) {
  const multi = isMultiGeo(geoType, geoCodes);
  const code = primaryFlagCode(geoCodes, geoType, countryHint);

  if (code && !multi) {
    return (
      <Flag code={code} size={size} height={size} shape="rounded" className={className} />
    );
  }

  // 多国：叠放前 2 面小旗 + 地球底
  const codes = (geoCodes || []).filter((c) => normalizeFlagCode(c)).slice(0, 2);
  if (multi && codes.length >= 2) {
    return (
      <span className={`flag-stack ${className}`} style={{ width: size, height: size }} aria-hidden>
        <Flag code={codes[0]} size={Math.round(size * 0.72)} height={Math.round(size * 0.52)} />
        <span className="flag-stack-second">
          <Flag code={codes[1]} size={Math.round(size * 0.72)} height={Math.round(size * 0.52)} />
        </span>
      </span>
    );
  }

  return (
    <span className={`pkg-flag-globe ${className}`} style={{ width: size, height: size }}>
      <IconGlobeSoft size={size} />
    </span>
  );
}
